import React, { useContext, useMemo, useState } from "react";
import PropTypes from "prop-types";

import {
  getButtonClassName,
  getButtonText,
} from "components/UserCardList/helpers/userCard";

import {
  APPLICATION_TAB_NAMES,
  JOB_APPLICATION_MATCH_STATUS,
  JOB_APPLICATION_MATCH_SUB_STATUS,
  USER_LIST_VIEWS,
} from "lookup";
import { AuthContext, ModalContext } from "context/providers";

import CustomerRateButton from "./CustomerRateButton";
import SvgIcon from "components/base/SvgIcon";
import ActionButton from "../ActionButton";
import AppDialog from "components/AppDialog";
import RejectionForm from "../RejectionForm";
import { CTA_TITLES } from "components/UserCardList/constants/ctaTitles";
import { getUserName } from "utils/helpers/users";
import CustomerRateModal from "./CustomerRateModal";
import { cn } from "utils";
import { isMatchPreShortlistedStatusType } from "utils/helpers/match";
import { FileText, Star, ThumbsDown, ThumbsUp } from "lucide-react";

const DEFAULT_BUTTON_CLASS_NAME =
  "transition-all whitespace-nowrap text-sm font-bold py-2 px-3 rounded-lg inline-flex items-center";

const MatchActionButtons = ({
  match,
  isLoading,
  isJobStatusActive,
  activeTabName,
  updateMatch,
  removeMatch,
  hasPrimaryMatcher,
}) => {
  const { user } = useContext(AuthContext);
  const { showModal } = useContext(ModalContext);
  const [showRejectModal, setShowRejectModal] = useState(false);

  const canBeFinalist =
    match.status === JOB_APPLICATION_MATCH_STATUS.APPLIED ||
    match.status === JOB_APPLICATION_MATCH_STATUS.MATCHED;

  const canMarkAccepted = canBeFinalist && user.canManuallyAcceptCandidates;

  const isNotFinalist =
    match?.subStatus !== JOB_APPLICATION_MATCH_SUB_STATUS.FINALIST;

  const { oneOfButtonsLoading, notesCount, canBeMatched, canBeSkipped } =
    useMemo(() => {
      const oneOfButtonsLoading = !!Object.values(isLoading).filter(
        (el) => !!el
      ).length;

      const { status, notesCount } = match;
      const canBeMatched =
        isMatchPreShortlistedStatusType(status) ||
        status === JOB_APPLICATION_MATCH_STATUS.SHORTLISTED ||
        status === JOB_APPLICATION_MATCH_STATUS.INTERESTED;

      const canBeSkipped =
        (activeTabName === APPLICATION_TAB_NAMES.SHORTLISTED ||
          activeTabName === APPLICATION_TAB_NAMES.PRECANDIDATES) &&
        (isMatchPreShortlistedStatusType(status) ||
          status === JOB_APPLICATION_MATCH_STATUS.SHORTLISTED ||
          status === JOB_APPLICATION_MATCH_STATUS.INTERESTED ||
          status === JOB_APPLICATION_MATCH_STATUS.INTERESTEDFASTTRACK);

      return { oneOfButtonsLoading, notesCount, canBeMatched, canBeSkipped };
    }, [isLoading, match, activeTabName]);

  const showRejectMatched = activeTabName === APPLICATION_TAB_NAMES.MATCHED;

  const handleRejectClick = () => {
    setShowRejectModal(true);
  };

  const [settingRate, setSettingRate] = useState(false);

  const { applicationId, jobOpportunityId } = match;
  const matchButtonTitle = !isJobStatusActive
    ? `${CTA_TITLES.match} This job is not active.`
    : !hasPrimaryMatcher
    ? `${CTA_TITLES.match} This job has no primary matcher.`
    : match.user?.hasActivePlacements
    ? `${CTA_TITLES.match} WARNING: User has active engagement(s).`
    : CTA_TITLES.match;
  const applyButtonTitle = !isJobStatusActive
    ? `${CTA_TITLES.apply} This job is not active.`
    : !hasPrimaryMatcher
    ? `${CTA_TITLES.apply} This job has no primary matcher.`
    : CTA_TITLES.apply;

  return (
    <>
      <div className="flex flex-wrap items-center gap-4">
        {canBeFinalist && (
          <button
            className={cn(
              DEFAULT_BUTTON_CLASS_NAME,
              "text-gray-600",
              isNotFinalist
                ? getButtonClassName(
                    "finalist",
                    isLoading[JOB_APPLICATION_MATCH_SUB_STATUS.FINALIST]
                  )
                : !isLoading[JOB_APPLICATION_MATCH_SUB_STATUS.FINALIST]
                ? "hover:!fill-red-500 hover:text-red-500"
                : "text-gray-600 hover:!text-gray-600"
            )}
            disabled={oneOfButtonsLoading}
            title={CTA_TITLES.finalist}
            onClick={() =>
              updateMatch({
                subStatus: isNotFinalist
                  ? JOB_APPLICATION_MATCH_SUB_STATUS.FINALIST
                  : JOB_APPLICATION_MATCH_SUB_STATUS.NULL,
              })
            }
          >
            <Star className={cn("h-5 w-5", !isNotFinalist && "fill-current")} />

            <span className="ml-2">
              {getButtonText(
                isNotFinalist ? "finalist" : "removeFinalist",
                isLoading[
                  isNotFinalist
                    ? JOB_APPLICATION_MATCH_SUB_STATUS.FINALIST
                    : JOB_APPLICATION_MATCH_SUB_STATUS.NULL
                ]
              )}
            </span>
          </button>
        )}

        {canBeMatched && (
          <ActionButton
            isDisabled={
              oneOfButtonsLoading || !isJobStatusActive || !hasPrimaryMatcher
            }
            isLoading={isLoading[JOB_APPLICATION_MATCH_STATUS.MATCHED]}
            action={() => {
              if (!match.user?.hasActivePlacements) {
                updateMatch({ status: JOB_APPLICATION_MATCH_STATUS.MATCHED });
              } else if (
                window.confirm(
                  "Are you sure you want to match this user, even though they have active engagement(s) currently?"
                )
              ) {
                updateMatch({ status: JOB_APPLICATION_MATCH_STATUS.MATCHED });
              }
            }}
            title={matchButtonTitle}
            className="text-gray-600"
            type="match"
            iconType={match.user?.hasActivePlacements ? "warning" : "heart"}
          />
        )}

        {match.status === JOB_APPLICATION_MATCH_STATUS.INTERESTEDFASTTRACK && (
          <ActionButton
            isDisabled={
              oneOfButtonsLoading || !isJobStatusActive || !hasPrimaryMatcher
            }
            isLoading={isLoading[JOB_APPLICATION_MATCH_STATUS.APPLIED]}
            action={() =>
              updateMatch({ status: JOB_APPLICATION_MATCH_STATUS.APPLIED })
            }
            title={applyButtonTitle}
            className="text-gray-600"
            type="apply"
            iconType="heart"
          />
        )}

        {!canBeMatched && (
          <CustomerRateButton
            setSettingRate={setSettingRate}
            match={match}
            disabled={oneOfButtonsLoading}
            viewType={USER_LIST_VIEWS.FULL_PROFILE}
            activeTabName={activeTabName}
          />
        )}

        {canMarkAccepted && (
          <button
            className={cn(
              DEFAULT_BUTTON_CLASS_NAME,
              "text-gray-600",
              getButtonClassName(
                "accept",
                isLoading[JOB_APPLICATION_MATCH_STATUS.ACCEPTED]
              )
            )}
            disabled={oneOfButtonsLoading}
            title={CTA_TITLES.accept}
            onClick={() =>
              updateMatch({ status: JOB_APPLICATION_MATCH_STATUS.ACCEPTED })
            }
          >
            <ThumbsUp className="w-[15px] h-[15px]" />

            <span className="ml-2">
              {getButtonText(
                "accept",
                isLoading[JOB_APPLICATION_MATCH_STATUS.ACCEPTED]
              )}
            </span>
          </button>
        )}

        {match.status !== JOB_APPLICATION_MATCH_STATUS.INTERESTED &&
          match.status !== JOB_APPLICATION_MATCH_STATUS.INTERESTEDFASTTRACK && (
            <button
              className={cn(
                DEFAULT_BUTTON_CLASS_NAME,
                "text-gray-600",
                getButtonClassName(
                  "delete",
                  isLoading[JOB_APPLICATION_MATCH_STATUS.DELETED]
                )
              )}
              disabled={oneOfButtonsLoading}
              onClick={removeMatch}
              title={CTA_TITLES.deleteMatch}
            >
              <SvgIcon type="delete" className="w-[15px] h-[15px]" />

              <span className="ml-2">
                {getButtonText(
                  "delete",
                  isLoading[JOB_APPLICATION_MATCH_STATUS.DELETED]
                )}
              </span>
            </button>
          )}

        {canBeSkipped && (
          <button
            className={cn(
              DEFAULT_BUTTON_CLASS_NAME,
              "h-9 w-9 justify-center px-0 text-gray-600",
              getButtonClassName(
                "skip",
                isLoading[JOB_APPLICATION_MATCH_STATUS.SKIPPED]
              )
            )}
            disabled={oneOfButtonsLoading}
            onClick={() =>
              updateMatch({ status: JOB_APPLICATION_MATCH_STATUS.SKIPPED })
            }
            title={CTA_TITLES.skip}
            aria-label={CTA_TITLES.skip}
          >
            <SvgIcon type="skip" className="w-5 h-5" />
          </button>
        )}

        {showRejectMatched && (
          <button
            className={cn(
              DEFAULT_BUTTON_CLASS_NAME,
              "text-gray-600",
              getButtonClassName(
                "reject",
                isLoading[JOB_APPLICATION_MATCH_STATUS.REJECTEDBYMATCHER]
              )
            )}
            disabled={oneOfButtonsLoading}
            title={CTA_TITLES.reject}
            onClick={handleRejectClick}
          >
            <ThumbsDown className="w-[20px] h-[20px]" />

            <span className="ml-2">
              {getButtonText(
                "reject",
                isLoading[JOB_APPLICATION_MATCH_STATUS.REJECTEDBYMATCHER]
              )}
            </span>
          </button>
        )}

        <button
          className={cn(
            DEFAULT_BUTTON_CLASS_NAME,
            "text-gray-600",
            getButtonClassName("note")
          )}
          title={CTA_TITLES.matchNotes}
          onClick={() =>
            showModal({
              type: "note",
              title: `Match Notes: ${getUserName(match.user)}`,
              applicationId: match.applicationId,
              isAllowedPublicNote: true,
            })
          }
        >
          <FileText className="w-[15px] h-[15px]" />

          <span className="ml-2">{`Notes${
            !!notesCount ? ` (${notesCount})` : ""
          }`}</span>
        </button>
      </div>
      <AppDialog
        open={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        header="Rejection Details"
        className="max-h-[90vh] w-[95%] max-w-none overflow-x-hidden rounded-lg md:w-10/12 lg:w-6/12"
        contentClassName="px-8 pb-8"
      >
        <RejectionForm
          applicationId={applicationId}
          jobOpportunityId={jobOpportunityId}
          isVisible={showRejectModal}
          onClose={() => setShowRejectModal(false)}
        />
      </AppDialog>
      <CustomerRateModal
        match={match}
        disabled={oneOfButtonsLoading}
        setSettingRate={setSettingRate}
        settingRate={settingRate}
      />
    </>
  );
};

MatchActionButtons.propTypes = {
  match: PropTypes.object,
  isLoading: PropTypes.object,
  callMatchAction: PropTypes.func,
  activeTabName: PropTypes.string,
};

MatchActionButtons.defaultProps = {
  match: {},
  isLoading: {},
  callMatchAction: () => {},
  activeTabName: "SEARCHRESULTS",
};

export default MatchActionButtons;
